import "./customize.css";
import type { InputFrame } from "@lache/sim";
import {
  type Appearance,
  DEFAULT_APPEARANCE,
  HAIR_COLORS,
  HAIRSTYLES,
  HATS,
  HEADBAND_COLORS,
  HEADBAND_STYLES,
} from "./appearance";
import { AppearancePreview } from "./appearance-preview";
import { revealMenuRow, setupMenuScroll } from "./menu-scroll";
import { updateLandscapeViewport } from "./orientation";

export interface CharacterScreenCallbacks {
  onBack(): void;
  /** Return persistence success, or update the status separately with setSaved. */
  onChange(appearance: Appearance): boolean | undefined;
}

/** One reusable character editor. Its preview exists only after the first visit. */
export class CharacterScreen {
  private readonly root = document.createElement("div");
  private readonly options: HTMLElement;
  private readonly previewElement: HTMLElement;
  private readonly status: HTMLElement;
  private readonly loading: HTMLElement;
  private readonly motion: HTMLButtonElement;
  private readonly zoom: HTMLButtonElement;
  private readonly back: HTMLButtonElement;
  private readonly reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
  private readonly lifetime = new AbortController();
  private readonly cancelScroll: () => void;
  private readonly groups: {
    key: keyof Appearance;
    buttons: HTMLButtonElement[];
    colorLabel: HTMLElement | null;
  }[] = [];
  private preview: AppearancePreview | null = null;
  private appearance = { ...DEFAULT_APPEARANCE };
  private paused = this.reducedMotion.matches;
  private closeUp = false;
  private ready = false;
  private frame = 0;
  private disposed = false;
  private suspended = false;
  private prevPause = true;
  private prevJump = true;
  private prevTuck = true;
  private prevDir = 0;
  private prevSide = 0;
  private repeatAt = 0;
  private repeatSideAt = 0;

  constructor(
    parent: HTMLElement,
    private readonly callbacks: CharacterScreenCallbacks,
  ) {
    this.root.className = "character-screen";
    this.root.hidden = true;
    this.root.innerHTML = `
      <main class="studio character-menu" aria-label="Character">
        <header class="studio-header">
          <h1>Character</h1>
          <button class="character-back" type="button">Back</button>
        </header>
        <div class="studio-body">
          <section class="wardrobe" aria-label="Character options">
            <section class="options" aria-label="Character choices"></section>
            <footer class="wardrobe-footer">
              <button class="reset" type="button">Reset</button>
              <span class="save-status" role="status">Saved on this device</span>
            </footer>
          </section>
          <section class="preview" aria-label="Character preview">
            <div class="preview-backdrop" aria-hidden="true"><div class="stage-grid"></div></div>
            <div class="preview-canvas" role="img" aria-label="Your ninja"></div>
            <p class="preview-status" role="status">Loading ninja…</p>
            <button class="zoom-button" type="button" aria-pressed="false">Close-up</button>
            <button class="motion-button" type="button" aria-pressed="false">Pause motion</button>
          </section>
        </div>
      </main>`;
    parent.append(this.root);
    this.options = this.root.querySelector<HTMLElement>(".options")!;
    this.previewElement = this.root.querySelector<HTMLElement>(".preview-canvas")!;
    this.status = this.root.querySelector<HTMLElement>(".save-status")!;
    this.loading = this.root.querySelector<HTMLElement>(".preview-status")!;
    this.motion = this.root.querySelector<HTMLButtonElement>(".motion-button")!;
    this.zoom = this.root.querySelector<HTMLButtonElement>(".zoom-button")!;
    this.back = this.root.querySelector<HTMLButtonElement>(".character-back")!;
    this.cancelScroll = setupMenuScroll(this.options, () => this.open);
    this.options.addEventListener("focusin", (event) => {
      if (event.target instanceof HTMLElement) revealMenuRow(event.target, this.options);
    });
    this.addOptions("hair", "Hair", HAIRSTYLES);
    this.addOptions("hairColor", "Hair color", HAIR_COLORS, true);
    this.addOptions("headband", "Headband", HEADBAND_STYLES);
    this.addOptions("headbandColor", "Band color", HEADBAND_COLORS, true);
    this.addOptions("hat", "Hat", HATS);
    this.back.addEventListener("click", () => this.callbacks.onBack());
    this.motion.addEventListener("click", () => {
      this.paused = !this.paused;
      this.updateMotion();
    });
    this.zoom.addEventListener("click", () => {
      this.closeUp = !this.closeUp;
      this.zoom.setAttribute("aria-pressed", String(this.closeUp));
      this.zoom.textContent = this.closeUp ? "Full body" : "Close-up";
      this.preview?.setCloseUp(this.closeUp);
      this.renderOnce();
    });
    this.root.querySelector(".reset")!.addEventListener("click", () => {
      this.appearance = { ...DEFAULT_APPEARANCE };
      this.apply(true);
    });
    this.root.addEventListener("keydown", (event) => {
      event.stopPropagation();
      if (event.key === "Escape") {
        event.preventDefault();
        this.callbacks.onBack();
      } else if (event.key === "Tab") {
        const buttons = Array.from(
          this.root.querySelectorAll<HTMLButtonElement>('button:not(:disabled):not([tabindex="-1"])'),
        );
        const first = buttons[0];
        const last = buttons.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus({ preventScroll: true });
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus({ preventScroll: true });
        }
      }
    });
    const signal = this.lifetime.signal;
    this.reducedMotion.addEventListener(
      "change",
      () => {
        this.paused = this.reducedMotion.matches;
        this.updateMotion();
      },
      { signal },
    );
    document.addEventListener("visibilitychange", this.resume, { signal });
    window.addEventListener("resize", this.resume, { signal });
    window.addEventListener("lacheviewportchange", this.resume, { signal });
    window.addEventListener(
      "pagehide",
      (event) => {
        if (!event.persisted) {
          this.destroy();
          return;
        }
        this.suspended = true;
        this.stop();
      },
      { signal },
    );
    window.addEventListener(
      "pageshow",
      () => {
        this.suspended = false;
        this.resume();
      },
      { signal },
    );
    this.apply(false);
    this.updateMotion();
  }

  get open(): boolean {
    return !this.disposed && !this.root.hidden;
  }

  show(appearance: Appearance): void {
    if (this.disposed) return;
    this.appearance = { ...appearance };
    this.root.hidden = false;
    this.prevPause = this.prevJump = this.prevTuck = true;
    this.prevDir = this.prevSide = 0;
    this.repeatAt = this.repeatSideAt = 0;
    this.apply(false);
    if (!this.preview) {
      this.preview = new AppearancePreview(this.previewElement);
      this.preview.setAppearance(this.appearance);
      this.preview.setReducedMotion(this.paused);
      this.preview.setCloseUp(this.closeUp);
      void this.preview
        .init()
        .then(() => {
          if (this.disposed) return;
          this.ready = true;
          this.loading.hidden = true;
          this.resume();
        })
        .catch((error: unknown) => {
          if (this.disposed) return;
          this.loading.textContent = "The preview couldn't start. Reload to try again.";
          console.error("Character preview failed", error);
        });
    }
    this.resume();
    this.back.focus({ preventScroll: true });
  }

  /** Dedicated controller frames only; keyboard navigation is handled by the controls themselves. */
  update(input: InputFrame, now: number): void {
    if (!this.open) return;
    const cancel = input.pause && !this.prevPause;
    const confirm = input.jump && !this.prevJump;
    const tuck = input.tuck && !this.prevTuck;
    this.prevPause = input.pause;
    this.prevJump = input.jump;
    this.prevTuck = input.tuck;
    const dir = input.moveY > 0.5 ? -1 : input.moveY < -0.5 ? 1 : 0;
    const side = input.moveX > 0.5 ? 1 : input.moveX < -0.5 ? -1 : 0;
    if (cancel || (tuck && !dir)) {
      this.callbacks.onBack();
      return;
    }
    if (confirm && !dir) {
      if (document.activeElement instanceof HTMLButtonElement && this.root.contains(document.activeElement)) {
        document.activeElement.click();
      }
      return;
    }
    if (dir && (dir !== this.prevDir || now >= this.repeatAt)) {
      const stops = Array.from(
        this.root.querySelectorAll<HTMLButtonElement>('button:not(:disabled):not([tabindex="-1"])'),
      );
      const index = stops.indexOf(document.activeElement as HTMLButtonElement);
      stops[(index + dir + stops.length) % stops.length]?.focus({ preventScroll: true });
      this.repeatAt = now + (dir !== this.prevDir ? 440 : 220);
    }
    if (side && (side !== this.prevSide || now >= this.repeatSideAt)) {
      const group = this.groups.find((entry) =>
        entry.buttons.includes(document.activeElement as HTMLButtonElement),
      );
      if (group) {
        const index = group.buttons.indexOf(document.activeElement as HTMLButtonElement);
        const next = group.buttons[(index + side + group.buttons.length) % group.buttons.length]!;
        next.focus({ preventScroll: true });
        next.click();
      }
      this.repeatSideAt = now + (side !== this.prevSide ? 440 : 220);
    }
    this.prevDir = dir;
    this.prevSide = side;
  }

  hide(): void {
    this.root.hidden = true;
    this.cancelScroll();
    this.stop();
  }

  setSaved(saved: boolean): void {
    this.status.textContent = saved ? "Saved on this device" : "Changes aren't saved";
  }

  info() {
    return {
      ready: this.ready,
      visible: this.open,
      paused: this.paused,
      closeUp: this.closeUp,
      rendering: this.frame !== 0,
      appearance: { ...this.appearance },
      preview: this.preview?.metadata ?? null,
    };
  }

  destroy(): void {
    if (this.disposed) return;
    this.hide();
    this.disposed = true;
    this.lifetime.abort();
    this.preview?.destroy();
    this.root.remove();
  }

  private addOptions(
    key: keyof Appearance,
    label: string,
    choices: readonly { value: string; label: string }[],
    color = false,
  ): void {
    const group = document.createElement("section");
    group.className = "option-group";
    const heading = document.createElement("div");
    heading.className = "option-heading";
    const title = document.createElement("h2");
    title.id = `character-label-${key}`;
    title.textContent = label;
    heading.append(title);
    const colorLabel = color ? document.createElement("span") : null;
    if (colorLabel) {
      colorLabel.className = "current-color";
      colorLabel.setAttribute("aria-hidden", "true");
      heading.append(colorLabel);
    }
    const row = document.createElement("div");
    row.className = `choices${color ? " swatches" : ""}`;
    row.setAttribute("role", "radiogroup");
    row.setAttribute("aria-labelledby", title.id);
    const buttons = choices.map((choice) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = color ? "swatch" : "choice";
      button.dataset.value = choice.value;
      button.setAttribute("role", "radio");
      button.setAttribute("aria-label", choice.label);
      button.title = choice.label;
      if (color) button.style.setProperty("--swatch", choice.value);
      else button.textContent = choice.label;
      button.addEventListener("click", (event) => {
        if (event.defaultPrevented) return;
        this.appearance = { ...this.appearance, [key]: choice.value };
        this.apply(true);
      });
      row.append(button);
      return button;
    });
    row.addEventListener("keydown", (event) => {
      const direction = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
      if (!direction && event.key !== "Home" && event.key !== "End") return;
      event.preventDefault();
      const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
      const next =
        event.key === "Home"
          ? 0
          : event.key === "End"
            ? buttons.length - 1
            : (index + (direction ?? 0) + buttons.length) % buttons.length;
      buttons[next]!.focus({ preventScroll: true });
      buttons[next]!.click();
    });
    group.append(heading, row);
    this.options.append(group);
    this.groups.push({ key, buttons, colorLabel });
  }

  private apply(save: boolean): void {
    for (const group of this.groups) {
      for (const button of group.buttons) {
        const selected = button.dataset.value === this.appearance[group.key];
        button.setAttribute("aria-checked", String(selected));
        button.tabIndex = selected ? 0 : -1;
        if (selected && group.colorLabel) group.colorLabel.textContent = button.title;
      }
    }
    if (save) {
      const saved = this.callbacks.onChange({ ...this.appearance });
      if (typeof saved === "boolean") this.setSaved(saved);
    }
    this.preview?.setAppearance(this.appearance);
    this.previewElement.setAttribute(
      "aria-label",
      `Your ninja: ${this.appearance.hair} hair, ${this.appearance.headband} headband, ${this.appearance.hat === "none" ? "no hat" : this.appearance.hat}`,
    );
    this.renderOnce();
  }

  private renderOnce(): void {
    if (this.ready && this.open && !this.suspended && !document.hidden) {
      this.preview?.update(performance.now());
    }
  }

  private readonly tick = (now: number): void => {
    this.frame = 0;
    if (!this.ready || !this.open || this.suspended || document.hidden) return;
    updateLandscapeViewport();
    this.preview?.update(now);
    if (!this.paused && !this.frame) this.frame = requestAnimationFrame(this.tick);
  };

  private stop(): void {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.preview?.pause();
  }

  private readonly resume = (): void => {
    this.stop();
    if (this.ready && this.open && !this.suspended && !document.hidden) {
      this.frame = requestAnimationFrame(this.tick);
    }
  };

  private updateMotion(): void {
    this.preview?.setReducedMotion(this.paused);
    this.motion.setAttribute("aria-pressed", String(this.paused));
    this.motion.textContent = this.paused ? "Resume motion" : "Pause motion";
    this.resume();
  }
}
